import { useState } from 'react';
import { Modal } from 'react-bootstrap';
import Form from 'react-bootstrap/Form';
import { Typography, NewIconButton, NewDropdown } from 'app/components/ui';
import { i18n } from 'app/utils/i18n';
import { measureLabel } from 'app/utils/unit-labels';
import ISO_CURRENCIES from 'app/views/documents/tagging/Taxonomies/currencyList.json';

import 'app/views/documents/tagging/Taxonomies/components/modals/Modal.scss';

export type UnitSpec = { measure: string } | { divideUnitNominator: string; divideUnitDenominator: string };

const XBRLI_MEASURES = ['xbrli:shares', 'xbrli:pure'];

interface UnitModalProps {
  show: boolean;
  defaultCurrency?: string | null;
  showApplyToggle?: boolean;
  onClose: () => void;
  onSave: (spec: UnitSpec, applyToTag: boolean) => void;
}

export const UnitModal = ({ show, defaultCurrency, showApplyToggle = false, onClose, onSave }: UnitModalProps) => {
  const defaultMeasure = defaultCurrency ? `iso4217:${defaultCurrency}` : 'iso4217:SEK';

  const [kind, setKind] = useState<'simple' | 'ratio'>('simple');
  const [measure, setMeasure] = useState<string>(defaultMeasure);
  const [nominator, setNominator] = useState<string>(defaultMeasure);
  const [denominator, setDenominator] = useState<string>('xbrli:shares');
  const [applyToTag, setApplyToTag] = useState(true);

  const measureOptions = [
    ...XBRLI_MEASURES.map((m) => ({ value: m, label: measureLabel(m) })),
    ...ISO_CURRENCIES.map((c: { code: string; name: string }) => ({ value: `iso4217:${c.code}`, label: `${c.code} - ${c.name}` })),
  ];

  const handleSave = () => {
    const spec: UnitSpec = kind === 'simple' ? { measure } : { divideUnitNominator: nominator, divideUnitDenominator: denominator };
    onSave(spec, showApplyToggle ? applyToTag : false);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleSave();
  };

  return (
    <Modal show={show} onHide={onClose} contentClassName="taxonomy-modal">
      <Modal.Body>
        <form onSubmit={handleSubmit} className="taxonomy-modal__content">
          <div className="taxonomy-modal__header">
            <Typography variant="body-8" fontWeight="semibold">
              {i18n('tag.create.custom.value')}
            </Typography>
            <NewIconButton icon="close" variant="transparent" type="button" onClick={onClose} />
          </div>

          <div className="form-group">
            <Typography variant="body-7" fontWeight="semibold">
              {i18n('tag.unit.type.title')}
            </Typography>
            <NewDropdown
              fullwidth
              options={[
                { value: 'simple', label: i18n('tag.unit.type.simple') },
                { value: 'ratio', label: i18n('tag.unit.type.ratio') },
              ]}
              currentValue={kind}
              onUpdate={(v: string) => setKind(v as 'simple' | 'ratio')}
            />
          </div>

          {kind === 'simple' ? (
            <div className="form-group">
              <Typography variant="body-7" fontWeight="semibold">
                {i18n('tag.unit.measure')}
              </Typography>
              <NewDropdown fullwidth options={measureOptions} currentValue={measure} onUpdate={setMeasure} />
            </div>
          ) : (
            <>
              <div className="form-group">
                <Typography variant="body-7" fontWeight="semibold">
                  {i18n('tag.unit.ratio.numerator')}
                </Typography>
                <NewDropdown fullwidth options={measureOptions} currentValue={nominator} onUpdate={setNominator} />
              </div>

              <div className="form-group">
                <Typography variant="body-7" fontWeight="semibold">
                  {i18n('tag.unit.ratio.denominator')}
                </Typography>
                <NewDropdown fullwidth options={measureOptions} currentValue={denominator} onUpdate={setDenominator} />
              </div>
            </>
          )}

          {showApplyToggle && (
            <div className="form-group flex-row" style={{ alignItems: 'center', gap: 8 }}>
              <Form.Check type="switch" id="unit-apply-switch" checked={applyToTag} onChange={(e) => setApplyToTag(e.target.checked)} />
              <Typography variant="body-7">{i18n('tag.unit.apply')}</Typography>
            </div>
          )}

          <div className="taxonomy-modal__footer">
            <button type="submit" className="btn btn-primary">
              {i18n('common.add')}
            </button>
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              {i18n('common.cancel')}
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
};
